// Service pour gérer les paramètres de l'application

import { historiqueService } from './HistoriqueService';
import { apiFetch } from './httpClient';

/**
 * Interface pour les tarifs et coefficients
 */
export interface TarifCoefficient {
  id?: string;
  nom: string;
  valeur: number;
  description?: string;
  categorie: 'main_oeuvre' | 'matiere' | 'accessoire' | 'coefficient';
}

/**
 * Interface pour les fournitures
 */
export interface Fourniture {
  id?: string;
  nom: string;
  type: string;
  prix: number;
  marge?: number; // Marge en pourcentage sur la fourniture
  unite: string;
  description?: string;
  reference?: string;
}

/**
 * Classe pour gérer les paramètres
 */
class ParametreService {

  /**
   * Récupère la liste des tarifs et coefficients
   */
  async getTarifsCoefficients(): Promise<TarifCoefficient[]> {
    try {
      const tarifsData = await apiFetch<any>('/tarifs', {
        signal: AbortSignal.timeout(10000) // 10 secondes timeout
      });
      
      // Convertir les données du backend en format TarifCoefficient[]
      const tarifsArray: TarifCoefficient[] = [];
      
      // Mapping des clés backend vers les tarifs
      const backendToTarifMapping: Record<string, { nom: string; categorie: 'main_oeuvre' | 'matiere' | 'accessoire' | 'coefficient' }> = {
        tarifHoraire: { nom: 'Tarif horaire Main d\'œuvre', categorie: 'main_oeuvre' },
        tempsCoutureMetre: { nom: 'Temps de couture par mètre', categorie: 'main_oeuvre' },
        tempsCoutureGlissiere: { nom: 'Temps couture glissière', categorie: 'main_oeuvre' },
        prixFermetureGlissiere: { nom: 'Prix fermeture à glissière', categorie: 'accessoire' },
        tauxTVAAchat: { nom: 'Taux de TVA achat marchandise', categorie: 'coefficient' },
        priseGabaritForfait: { nom: 'Prise de gabarit Forfait mini', categorie: 'main_oeuvre' },
        priseGabaritTarifM2: { nom: 'Prise de gabarit Tarif / m²', categorie: 'main_oeuvre' },
        mousseSouple: { nom: 'Mousse souple', categorie: 'matiere' },
        mousseFerme: { nom: 'Mousse ferme', categorie: 'matiere' },
        mousseTresFerme: { nom: 'Mousse très ferme', categorie: 'matiere' },
        majorationSurpiqurePaspoil: { nom: 'Majoration pour surpiqûre ou passepoil', categorie: 'coefficient' },
        majorationFormeComplexe: { nom: 'Majoration pour forme complexe', categorie: 'coefficient' },
        tempsPreparationBase: { nom: 'Temps de préparation et finition - Base', categorie: 'main_oeuvre' },
        tempsPreparationRectJoues: { nom: 'Temps de préparation - Rectangle avec joues', categorie: 'main_oeuvre' },
        tempsPreparationCylindreJoues: { nom: 'Temps de préparation - Cylindre avec joues', categorie: 'main_oeuvre' },
        tempsPreparationParM2: { nom: 'Temps de préparation - Par m² supplémentaire', categorie: 'main_oeuvre' },
        tempsPreparationFermeture: { nom: 'Temps de préparation - Fermeture', categorie: 'main_oeuvre' },
        tempsPreparationMousse: { nom: 'Temps de préparation - Mousse', categorie: 'main_oeuvre' },
        tempsPreparationSurpiqure: { nom: 'Temps de préparation - Surpiqûre', categorie: 'main_oeuvre' },
        tempsPreparationFormeComplexe: { nom: 'Temps de préparation - Forme complexe', categorie: 'main_oeuvre' },
        tempsPreparationMatDifferentes: { nom: 'Temps de préparation - Matières différentes', categorie: 'main_oeuvre' },
        tarifCoutureRenfortsBache: { nom: 'Tarif couture renforts pour bâches', categorie: 'main_oeuvre' },
        tarifCoutureSanglesBache: { nom: 'Tarif coutures sangles pour bâches', categorie: 'main_oeuvre' },
        prixSangleML: { nom: 'Prix sangle / ML', categorie: 'matiere' },
        prixOeilletUnitaire: { nom: 'Prix unitaire d\'un œillet', categorie: 'accessoire' },
        tempsPoseOeillet: { nom: 'Temps de pose d\'un œillet', categorie: 'main_oeuvre' },
        majorationAssemblageBache: { nom: 'Majoration pour assemblage de bâche', categorie: 'coefficient' },
        tempsAssemblageBacheM2: { nom: 'Temps d\'assemblage par m²', categorie: 'main_oeuvre' },
      };
      
      // Convertir les données du backend
      Object.entries(tarifsData).forEach(([key, value]) => {
        const mapping = backendToTarifMapping[key];
        if (mapping && typeof value === 'number') {
          tarifsArray.push({
            id: `tarif-${key}`,
            nom: mapping.nom,
            valeur: value,
            categorie: mapping.categorie
          });
        }
      });
      
      return tarifsArray;
      
    } catch (error) {
      console.error('Erreur lors de la récupération des tarifs depuis le backend:', error);
      
      // En cas d'erreur, essayer le localStorage comme fallback
      const storedTarifs = localStorage.getItem('tarifs_coefficients');
      if (storedTarifs) {
        return JSON.parse(storedTarifs);
      }
      
      // Si aucun tarif n'est stocké, utiliser les données de démonstration
      const demoTarifs = this.getTarifsCoefficientsDemos();
      return demoTarifs;
    }
  }

  /**
   * Sauvegarde un tarif ou coefficient
   */
  async saveTarifCoefficient(tarifCoefficient: TarifCoefficient): Promise<TarifCoefficient> {
    try {
      // Simuler un délai réseau
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Récupérer les tarifs et coefficients existants
      const storedTarifs = localStorage.getItem('tarifs_coefficients');
      let tarifs: TarifCoefficient[] = storedTarifs ? JSON.parse(storedTarifs) : [];
      
      // Vérifier si le tarif existe déjà
      const existingIndex = tarifs.findIndex(t => 
        t.nom === tarifCoefficient.nom && t.categorie === tarifCoefficient.categorie
      );
      
      if (existingIndex >= 0) {
        // Mettre à jour le tarif existant
        tarifs[existingIndex] = {
          ...tarifs[existingIndex],
          valeur: tarifCoefficient.valeur,
          description: tarifCoefficient.description
        };
      } else {
        // Ajouter un nouveau tarif
        const newTarif = {
          ...tarifCoefficient,
          id: tarifCoefficient.id || `tarif-${Date.now()}`
        };
        tarifs.push(newTarif);
      }
      
      // Stocker les tarifs mis à jour dans le localStorage
      localStorage.setItem('tarifs_coefficients', JSON.stringify(tarifs));
      
      // Retourner le tarif sauvegardé
      return tarifCoefficient.id 
        ? tarifs.find(t => t.id === tarifCoefficient.id)! 
        : tarifs.find(t => t.nom === tarifCoefficient.nom && t.categorie === tarifCoefficient.categorie)!;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du tarif ou coefficient:', error);
      // Simuler la sauvegarde en cas d'erreur
      return {
        ...tarifCoefficient,
        id: tarifCoefficient.id || `tarif-${Date.now()}`,
      };
    }
  }

  /**
   * Sauvegarde tous les tarifs et coefficients en une seule opération
   */
  async saveTarifsCoefficients(formData: any): Promise<any> {
    try {
      // Convertir les données du formulaire en un objet simple
      const tarifsData: { [key: string]: any } = {};
      
      // Parcourir toutes les clés du formulaire
      Object.entries(formData).forEach(([key, value]) => {
        // Utiliser directement la clé comme nom de tarif
        tarifsData[key] = value;
      });
      
      // Stocker les tarifs dans le localStorage pour la persistance locale
      localStorage.setItem('tarifs_coefficients_raw', JSON.stringify(tarifsData));

      const result = await apiFetch<any>('/tarifs', {
        method: 'PUT',
        body: JSON.stringify(tarifsData)
      });

      // Enregistrer la modification dans l'historique
      try {
        await historiqueService.enregistrerModification(
          'tarifs_coefficients',
          'Mise à jour des tarifs et coefficients',
          'Utilisateur'
        );
      } catch (historyError) {
        console.warn('⚠️ Erreur lors de l\'enregistrement dans l\'historique:', historyError);
        // Ne pas faire échouer la sauvegarde si l'historique échoue
      }
      
      return result;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des tarifs:', error);
      throw error;
    }
  }

  /**
   * Récupère la liste des fournitures
   */
  async getFournitures(): Promise<Fourniture[]> {
    const data = await apiFetch<any>('/fermetures/disponibles');

    // Validation des données reçues
    if (!data || typeof data !== 'object') {
      throw new Error('Réponse API invalide : données manquantes');
    }

    if (!Array.isArray(data.fermetures)) {
      console.warn('Format de données inattendu:', data);
      throw new Error('Format de données invalide : fermetures non trouvées');
    }

    // Convertir les fermetures avec validation
    const fermetures = data.fermetures
      .filter((f: any) => f && typeof f === 'object')
      .map((f: any) => {
        // Validation des champs requis
        if (!f.id || !f.nom || !f.type) {
          console.warn('Fermeture invalide ignorée:', f);
          return null;
        }
        
        return {
          id: f.id,
          nom: f.nom,
          type: f.type,
          taille: typeof f.taille === 'number' ? f.taille : 0,
          prix: typeof f.prix === 'number' ? f.prix : 0,
          unite: f.unite || 'unité',
          reference: f.reference || '',
          description: f.description || '',
          marge: typeof f.remise_fournisseur === 'number' ? f.remise_fournisseur : 50
        };
      })
      .filter(Boolean);

    // Récupérer les autres fournitures depuis le localStorage
    let autresFournitures: Fourniture[] = [];
    const storedFournitures = localStorage.getItem('fournitures');
    if (storedFournitures) {
      const allStored = JSON.parse(storedFournitures);
      // Filtrer pour exclure les fermetures
      autresFournitures = allStored.filter((f: Fourniture) => 
        f.type !== 'glissiere' && f.type !== 'velcro' && f.type !== 'fermeture'
      );
    } else {
      // Si aucune fourniture n'est stockée, utiliser les données de démonstration
      const demoFournitures = this.getFournituresDemos();
      autresFournitures = demoFournitures.filter((f: Fourniture) => 
        f.type !== 'glissiere' && f.type !== 'velcro' && f.type !== 'fermeture'
      );
      
      // Stocker les données de démonstration dans le localStorage
      localStorage.setItem('fournitures', JSON.stringify(autresFournitures));
    }
    
    // Combiner les fermetures de l'API et les autres fournitures du localStorage
    const toutesLesFournitures = [...fermetures, ...autresFournitures];
    
    return toutesLesFournitures;
  }

  /**
   * Sauvegarde une fourniture
   */
  async saveFourniture(fourniture: Fourniture): Promise<Fourniture> {
    try {
      // Simuler un délai réseau
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Récupérer les fournitures existantes
      const storedFournitures = localStorage.getItem('fournitures');
      let fournitures: Fourniture[] = storedFournitures ? JSON.parse(storedFournitures) : [];
      
      if (fourniture.id) {
        // Mettre à jour une fourniture existante
        const index = fournitures.findIndex(f => f.id === fourniture.id);
        if (index >= 0) {
          fournitures[index] = fourniture;
        } else {
          fournitures.push(fourniture);
        }
      } else {
        // Ajouter une nouvelle fourniture
        const newFourniture = {
          ...fourniture,
          id: `fourniture-${Date.now()}`
        };
        fournitures.push(newFourniture);
      }
      
      // Stocker les fournitures mises à jour dans le localStorage
      localStorage.setItem('fournitures', JSON.stringify(fournitures));
      
      // Retourner la fourniture sauvegardée
      return fourniture.id 
        ? fournitures.find(f => f.id === fourniture.id)! 
        : fournitures[fournitures.length - 1];
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la fourniture:', error);
      // Simuler la sauvegarde en cas d'erreur
      return {
        ...fourniture,
        id: fourniture.id || `fourniture-${Date.now()}`,
      };
    }
  }

  /**
   * Supprime un tarif ou coefficient
   */
  async deleteTarifCoefficient(id: string): Promise<void> {
    try {
      // Simuler un délai réseau
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Récupérer les tarifs et coefficients existants
      const storedTarifs = localStorage.getItem('tarifs_coefficients');
      if (!storedTarifs) return;
      
      let tarifs: TarifCoefficient[] = JSON.parse(storedTarifs);
      
      // Supprimer le tarif
      tarifs = tarifs.filter(t => t.id !== id);
      
      // Stocker les tarifs mis à jour dans le localStorage
      localStorage.setItem('tarifs_coefficients', JSON.stringify(tarifs));
    } catch (error) {
      console.error('Erreur lors de la suppression du tarif ou coefficient:', error);
      // Ne rien faire en cas d'erreur
    }
  }

  /**
   * Supprime une fourniture
   */
  async deleteFourniture(id: string): Promise<void> {
    try {
      // Simuler un délai réseau
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Récupérer les fournitures existantes
      const storedFournitures = localStorage.getItem('fournitures');
      if (!storedFournitures) return;
      
      let fournitures: Fourniture[] = JSON.parse(storedFournitures);
      
      // Supprimer la fourniture
      fournitures = fournitures.filter(f => f.id !== id);
      
      // Stocker les fournitures mises à jour dans le localStorage
      localStorage.setItem('fournitures', JSON.stringify(fournitures));
    } catch (error) {
      console.error('Erreur lors de la suppression de la fourniture:', error);
      // Ne rien faire en cas d'erreur
    }
  }

  /**
   * Retourne des tarifs et coefficients de démonstration
   */
  private getTarifsCoefficientsDemos(): TarifCoefficient[] {
    return [
      {
        id: '1',
        nom: 'Taux horaire main d\'œuvre',
        valeur: 45,
        description: 'Taux horaire pour la main d\'œuvre',
        categorie: 'main_oeuvre',
      },
      {
        id: '2',
        nom: 'Coefficient de majoration surpiqûre',
        valeur: 2.0,
        description: 'Coefficient de majoration pour la surpiqûre',
        categorie: 'coefficient',
      },
      {
        id: '3',
        nom: 'Coefficient de majoration forme complexe',
        valeur: 1.5,
        description: 'Coefficient de majoration pour les formes complexes',
        categorie: 'coefficient',
      },
      {
        id: '4',
        nom: 'Temps forfaitaire gabarit',
        valeur: 30,
        description: 'Temps forfaitaire en minutes pour la création d\'un gabarit',
        categorie: 'main_oeuvre',
      },
      {
        id: '5',
        nom: 'Temps forfaitaire pose fermeture',
        valeur: 15,
        description: 'Temps forfaitaire en minutes pour la pose d\'une fermeture à glissière',
        categorie: 'main_oeuvre',
      },
      {
        id: '6',
        nom: 'Temps couture par mètre',
        valeur: 10,
        description: 'Temps en minutes pour coudre un mètre',
        categorie: 'main_oeuvre',
      },
    ];
  }

  /**
   * Retourne des fournitures de démonstration
   */
  private getFournituresDemos(): Fourniture[] {
    return [
      {
        id: '1',
        nom: 'Fermeture à glissière',
        type: 'fermeture',
        prix: 8.50,
        unite: 'unité',
        description: 'Fermeture à glissière standard',
      },
      {
        id: '2',
        nom: 'Mousse souple',
        type: 'mousse',
        prix: 120,
        unite: 'm³',
        description: 'Mousse souple pour coussins',
      },
      {
        id: '3',
        nom: 'Mousse ferme',
        type: 'mousse',
        prix: 150,
        unite: 'm³',
        description: 'Mousse ferme pour coussins',
      },
      {
        id: '4',
        nom: 'Mousse haute densité',
        type: 'mousse',
        prix: 180,
        unite: 'm³',
        description: 'Mousse haute densité pour coussins',
      },
      {
        id: '5',
        nom: 'Fil à coudre',
        type: 'fil',
        prix: 0.50,
        unite: 'm',
        description: 'Fil à coudre standard',
      },
    ];
  }
}

// Exporter une instance unique du service
export const parametreService = new ParametreService();
